import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackFormSubmission, trackFormStart, trackFormError } from './analytics';

/**
 * Contact Form Component with Analytics Tracking
 * 
 * Sends emails via Netlify Function + Resend API
 * 
 * Tracks:
 * - Form start (when user begins filling out)
 * - Form submission (success/failure)
 * - Form errors
 * - Newsletter signups
 */

function ContactForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    newsletterOptin: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  const handleChange = (e) => {
    // Track form start on first interaction
    if (!formStarted) {
      setFormStarted(true);
      trackFormStart('contact_form');
    }
    
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Call Netlify Function to send email via Resend
      const response = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          newsletterOptin: formData.newsletterOptin
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      // Track successful submission
      trackFormSubmission('contact_form', true);
      
      // Track newsletter signup if opted in
      if (formData.newsletterOptin) {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'newsletter_signup',
            email: formData.email
          });
        }
      }
      
      // Redirect to thank you page
      navigate('/thank-you');
    } catch (error) {
      // Track form error
      trackFormError('contact_form', error.message || 'Unknown error');
      trackFormSubmission('contact_form', false);
      
      setError('Something went wrong. Please try again or call us directly at (312) 675-6000.');
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl p-8 shadow-soft">
      <h2 className="text-3xl font-bold text-blue-900 mb-2">Contact Us</h2>
      <p className="text-gray-600 mb-6">Get a free consultation for your commercial construction project.</p>
      
      {success && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <div>
              <p className="font-semibold">Thank you for your message!</p>
              <p className="text-sm">We'll get back to you within 1 business day.</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <p className="font-semibold">Unable to send message</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Your full name"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your@email.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(123) 456-7890"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows="5"
            placeholder="Tell us about your project..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all resize-none"
          />
        </div>

        {/* Newsletter Opt-in Checkbox */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative flex items-center justify-center mt-0.5">
              <input
                type="checkbox"
                name="newsletterOptin"
                checked={formData.newsletterOptin}
                onChange={handleChange}
                className="w-5 h-5 text-blue-900 border-gray-300 rounded focus:ring-blue-900 focus:ring-2 cursor-pointer"
              />
            </div>
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-700 group-hover:text-blue-900 transition-colors">
                Subscribe to our newsletter
              </span>
              <p className="text-xs text-gray-500 mt-1">
                Get industry insights, project spotlights, and company updates delivered to your inbox. 
                We respect your privacy and you can unsubscribe anytime.
              </p>
            </div>
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-900 to-blue-800 hover:from-red-600 hover:to-red-700 text-white font-medium py-3 px-6 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              Send Message
            </>
          )}
        </button>

        <p className="text-xs text-gray-500 text-center mt-4">
          By submitting this form, you agree to our{' '}
          <a href="/privacy-policy" className="text-blue-900 hover:underline">Privacy Policy</a>.
        </p>
      </form>
    </div>
  );
}

export default ContactForm;
